import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { OrgUserProfile } from 'interfaces/profile.interface';
import { LOCATION_TYPES, LocationTypesArrayValue } from 'services/locations/locations.constant';
import { SmartModalService } from '@certemy/ui-components';
import { LocationsService } from 'services/locations/locations.service';
import _get from 'lodash-es/get';
import { Store } from '@ngrx/store';
import { Selectors as LabelSelectors } from 'features/global-organizations-setting-state/global-organizations-setting.state';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  GlobalOrganizationsLabel,
  OrganizationsLabelType,
} from 'features/global-organizations-setting-state/global-organizations-setting.constant';
import { take } from 'rxjs/operators';
import { ROLE_CODES } from 'constants/role.constants';
import { sortStringArray } from 'utils/helpers';

@UntilDestroy()
@Component({
  selector: 'cm-profile-location-info',
  templateUrl: './profile-location-info.component.html',
  styleUrls: ['./profile-location-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileLocationInfoComponent implements OnInit, OnChanges, OnDestroy {
  _profile: OrgUserProfile;
  countries: LocationTypesArrayValue;
  regions: LocationTypesArrayValue;
  offices: LocationTypesArrayValue;
  firstOffice: any;
  organizationLabels: GlobalOrganizationsLabel;
  arr: any[];

  readonly LOCATION_TYPES = LOCATION_TYPES;

  @Input() profile;
  @ViewChild('fullListTemplate') fullListTemplate: TemplateRef<null>;

  constructor(
    private locationsService: LocationsService,
    private smartModalService: SmartModalService,
    private store: Store<any>,
  ) {}

  ngOnInit() {
    this.store
      .select(LabelSelectors.labels)
      .pipe(untilDestroyed(this))
      .subscribe((labels: GlobalOrganizationsLabel) => {
        this.organizationLabels = labels;
      });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.profile && changes.profile.currentValue !== changes.profile.previousValue) {
      const profile = changes.profile.currentValue;
      this._profile = profile;
      const locations = profile.locations;
      const additionalLocations = profile.additionalLocations;
      if (_get(locations, 'firstOffice.office')) {
        this.firstOffice = locations.firstOffice;
        if (profile.role_id === ROLE_CODES.PROFESSIONAL && additionalLocations) {
          const { countries, regions, offices } = additionalLocations;
          this.countries = {
            ...countries,
            names: this.getUniqueNames(sortStringArray([locations.firstOffice.country.name, ...countries.names])),
          };
          this.regions = {
            ...regions,
            names: this.getUniqueNames(sortStringArray([locations.firstOffice.region.name, ...regions.names])),
          };
          this.offices = {
            ...offices,
            names: this.getUniqueNames(sortStringArray([locations.firstOffice.office.name, ...offices.names])),
          };
        } else {
          const { countries, regions, offices } = locations;
          this.countries = countries;
          this.regions = regions;
          this.offices = offices;
        }
      }
    }
  }

  ngOnDestroy() {}

  showFullList(type: LOCATION_TYPES) {
    let typeLabel = '';
    switch (type) {
      case LOCATION_TYPES.COUNTRY:
        typeLabel = 'Countries';
        this.arr = this.countries.names;
        break;
      case LOCATION_TYPES.REGION:
        typeLabel = `${this.organizationLabels[OrganizationsLabelType.LOCATION_REGION]}(s)`;
        this.arr = this.regions.names;
        break;
      case LOCATION_TYPES.OFFICE:
        typeLabel = `${this.organizationLabels[OrganizationsLabelType.LOCATION_OFFICE]}(s)`;
        this.arr = this.offices.names;
        break;
    }
    this.smartModalService
      .openNotification(
        {
          title: `${this._profile.first_name} ${this._profile.last_name}'s ${typeLabel}`,
          body: this.fullListTemplate,
          closeButtonText: 'Ok',
        },
        {
          size: 'lg',
        },
      )
      .pipe(take(1))
      .subscribe();
  }

  getUniqueNames(names: string[]): string[] {
    const uniqueNames = new Set(names);
    return [...uniqueNames];
  }
}
